import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { epochLabel } from '@/features/projects/format'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  loadPayload,
  loadSnapshots,
  selectConfigPayload,
  selectSnapshots,
  selectSnapshotsNonce
} from '../slices/phaseGatesSlice'
import type { ConfigPayloadView } from '../types'

/**
 * The step-04 snapshot panel (PX-W6.3, AC-3): every locked configuration
 * version (newest first) plus the review payload — LIVE (`locked=false`)
 * while the gate is open, the FROZEN render once approved. Later reads return
 * exactly what was locked (the digest binds them); nothing here re-renders a
 * frozen version client-side.
 */

function PayloadView({ payload, title }: { payload: ConfigPayloadView | null; title: string }) {
  const { t } = useTranslation('projectWorkspace')
  if (payload === null) {
    return <p className="mt-1 text-sm text-muted-foreground">{t('gates.dialog.payloadDigestLoading')}</p>
  }
  const surfaces = payload.surfaces ?? []
  return (
    <div className="mt-2 rounded-md border border-border px-3 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <span className="truncate font-mono text-xs text-muted-foreground" title={payload.payloadDigest}>
          {payload.payloadDigest}
        </span>
      </div>
      {payload.locked && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('snapshots.lockedBy', { name: payload.lockedByName ?? payload.lockedBy ?? '—' })} ·{' '}
          {t('snapshots.lockedAt', { date: epochLabel(payload.lockedAt) })}
        </p>
      )}
      <ul className="mt-1.5">
        {surfaces.map((surface) => (
          <li key={surface.surface} className="v-row py-1 text-sm">
            {surface.present ? (
              <span className="text-foreground">
                {t('snapshots.surfacePresent', { label: surface.label ?? surface.surface })}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {t('snapshots.surfaceAbsent', {
                  label: surface.label ?? surface.surface,
                  reason: surface.note ?? '—'
                })}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The panel OWNS its data path (review M1): it loads the snapshot list and
 * the review payload on its own mount — guarded once per project by the
 * slice — and refetches when a flow.04 approval re-arms the cache
 * (`snapshotsNonce`). Before this fix the only loader lived in the decision
 * dialog, which can never open once flow.04 is decided: the panel rendered
 * permanently empty on fresh mounts and AC-3's "later reads return exactly
 * the recorded snapshot" had no client path.
 */
export function ConfigSnapshotPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const snapshots = useAppSelector(selectSnapshots)
  const payload = useAppSelector(selectConfigPayload)
  const snapshotsNonce = useAppSelector(selectSnapshotsNonce)
  const [showPayload, setShowPayload] = useState(false)

  // Mount + re-arm refresh: the once-per-project guard lives in the thunk's
  // condition; the nonce moves only when a new version was just locked.
  useEffect(() => {
    void dispatch(loadSnapshots({ orgId, projectId }))
    void dispatch(loadPayload({ orgId, projectId, gateRef: 'flow.04' }))
  }, [dispatch, orgId, projectId, snapshotsNonce])

  return (
    <section aria-label={t('snapshots.panelTitle')} className="rounded-lg border border-border bg-card px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <SectionLabel>{t('snapshots.panelTitle')}</SectionLabel>
        <button
          type="button"
          onClick={() => setShowPayload((value) => !value)}
          className="text-xs font-medium text-accent-foreground hover:underline"
        >
          {showPayload ? t('snapshots.hidePayload') : t('snapshots.showPayload')}
        </button>
      </div>

      {snapshots.length === 0 ? (
        <p className="mt-1.5 text-sm text-muted-foreground">{t('snapshots.empty')}</p>
      ) : (
        <ul className="mt-1.5" data-testid="config-snapshots">
          {snapshots.map((row) => (
            <li key={row.version} className="v-row flex flex-wrap items-center gap-x-3 gap-y-0.5 py-2">
              <span className="v-figure text-sm font-semibold tabular-nums text-foreground">
                {t('snapshots.version', { version: row.version })}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('snapshots.lockedBy', { name: row.lockedByName ?? row.lockedBy ?? '—' })} ·{' '}
                {t('snapshots.lockedAt', { date: epochLabel(row.lockedAt) })}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground" title={row.payloadDigest}>
                {row.payloadDigest}
              </span>
            </li>
          ))}
        </ul>
      )}

      {showPayload && (
        <PayloadView
          payload={payload}
          title={payload?.locked ? t('snapshots.payloadFrozen') : t('snapshots.payloadLive')}
        />
      )}
    </section>
  )
}
